<template>
  <div class="w-full py-16 bg-vp-base-100 p-8">
    <div class="mx-auto w-full sm:w-10/12 md:w-8/12 lg:w-4/12 xl:3/12 2xl:w-2/12">
      <h1 class="text-2xl my-8 text-center">
        {{ $gettext('Change Password') }}
      </h1>
      <VPLabel />
      <VPLabel />
      <Form
        :form-id="formId"
        class="vp-login-form"
        @submit="onSubmit"
      >
        <VPInput
          input-id="email"
          :label="$gettext('Email')"
          :placeholder="$gettext('Enter your email address')"
          autocomplete="username"
          autocapitalize="off"
          autocorrect="off"
          :disabled="loading"
        />
        <VPInput
          input-id="currentPassword"
          :label="$gettext('Current password')"
          :placeholder="$gettext('Enter your current password')"
          type="password"
          input-element-type="password"
          autocomplete="current-password"
          :disabled="loading"
        />
        <VPInput
          input-id="newPassword"
          :label="$gettext('New password')"
          :placeholder="$gettext('Enter your new password')"
          type="password"
          input-element-type="password"
          :disabled="loading"
        />
        <VPInput
          input-id="confirmNewPassword"
          :label="$gettext('Confirm new password')"
          :placeholder="$gettext('Confirm new password')"
          type="password"
          input-element-type="password"
          :disabled="loading"
        />
        <VPButton
          type="submit"
          class="my-4 block w-full disabled:cursor-not-allowed"
          :disabled="loading"
          @submit="onSubmit"
        >
          {{ $gettext('Change Password') }}
        </VPButton>
      </Form>
    </div>
  </div>
</template>

<script setup lang='ts'>
import { useRootStore } from '@/stores/root';
import { ref } from 'vue';
import { Form, useFormsStoreComposable } from 'vuniform';
import VPButton from '@/components/form/VPButton.vue';
import VPInput from '@/components/form/VPInput.vue';
import { Auth } from '@vp/common';
import { useGettext } from 'vue3-gettext';
import { useToast } from 'vue-toastification';

const rootStore = useRootStore();
const loading = ref<boolean>(false);
const showConfirmation = ref<boolean>(false);
const toast = useToast();
const { cognito } = rootStore.config!;
const { $gettext } = useGettext();

const { formId, formGetValues, formsStore } = useFormsStoreComposable('change-password-form');

const onSubmit = async () => {
  loading.value = true;
  showConfirmation.value = false;
  formsStore.CLEAR_ERRORS(formId);

  const { email, currentPassword, newPassword, confirmNewPassword } = formGetValues<{ email: string; currentPassword: string; newPassword: string; confirmNewPassword: string }>();

  if (!cognito || !cognito.clientId || !cognito.region) {
    throw new Error('Cognito config is missing');
  }

  if (newPassword !== confirmNewPassword) {
    formsStore.SET_ERRORS({
      requesterId: formId,
      errors: {
        confirmNewPassword: [$gettext('Passwords do not match')],
      },
    });
    loading.value = false;
    return;
  }

  try {
    const { AccessToken } = await Auth.getCognitoToken(email, currentPassword, { region: cognito.region, clientId: cognito.clientId });

    await Auth.cognitoChangePassword(currentPassword, newPassword, AccessToken, { region: cognito.region });

    toast.success($gettext('Password changed successfully'));
    formsStore.RESET_FIELD_VALUES(formId);
  } catch (e) {
    formsStore.SET_ERRORS({
      requesterId: formId,
      errors: {
        confirmNewPassword: [e.message],
      },
    });
  } finally {
    loading.value = false;
  }
};

</script>
